import axios from "axios";

const OWN_HOST = /(^|\.)trustlogisticsllc\.org$|(^|\.)trustlogistics\.org$|\.emergent\.host$/;
export const BASE = OWN_HOST.test(window.location.hostname) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

let refreshing: Promise<any> | null = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const url: string = cfg.url || "";
    if (err.response?.status === 401 && !cfg._retry && !url.includes("/auth/")) {
      cfg._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(cfg);
      } catch {
        refreshing = null;
      }
    }
    return Promise.reject(err);
  },
);

export function errMsg(e: any): string {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? String(x.msg).replace("Value error, ", "") : JSON.stringify(x))).join(" ");
  return d.msg || String(d);
}

export const mediaUrl = (u?: string) => (!u ? "" : u.startsWith("/api/") ? `${BASE}${u}` : u);

export const money = (n: number, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD", maximumFractionDigits: 2 }).format(n || 0);

export const today = () => new Date().toISOString().slice(0, 10);
export const addDays = (d: string, n: number) => {
  const x = new Date(d + "T00:00:00Z");
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
export const fmtDate = (d?: string) =>
  d ? new Date(d.length === 10 ? d + "T00:00:00" : d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

export type Kind = "stay" | "vehicle";

export interface Listing {
  id: string;
  title: string;
  city?: string;
  area?: string;
  description?: string;
  images?: string[];
  rate?: number;
  currency?: string;
  service_fee?: number;
  fee_label?: string;
  security_deposit?: number;
  min_units?: number;
  max_units?: number;
  capacity?: number;
  amenities?: string[];
  published?: boolean;
  featured?: boolean;
  bedrooms?: number;
  bathrooms?: number;
  property_type?: string;
  house_rules?: string;
  check_in?: string;
  check_out?: string;
  make?: string;
  model?: string;
  year?: number;
  transmission?: string;
  fuel?: string;
  min_driver_age?: number;
  mileage_policy?: string;
  pickup_options?: string;
  duration?: string;
  price_from?: number;
  includes?: string;
  weekly_rate?: number;
  monthly_rate?: number;
  image_meta?: { url: string; caption?: string; alt?: string }[];
  category?: string;
  vehicle_class?: string;
  status?: string;
  country?: string;
  cancellation_policy?: string;
  extra_charges?: string;
  addons?: string[];
  fuel_policy?: string;
  insurance_requirements?: string;
  return_options?: string;
  airport_pickup?: boolean;
  delivery?: boolean;
  unavailable?: { start: string; end: string }[];
  blocked_dates?: { start: string; end: string; reason?: string }[];
}

export interface Location { id: string; service: "stays" | "vehicles"; city: string; title: string; country?: string; status: "available" | "coming_soon"; description?: string; image?: string; order?: number }

export const metaFor = (item: Listing, url?: string) => item.image_meta?.find((m) => m.url === url);

export interface Booking {
  id: string;
  reference: string;
  kind: Kind;
  item_id: string;
  item_title: string;
  city?: string;
  start_date: string;
  end_date: string;
  units: number;
  unit_label: string;
  guests: number;
  pricing: { rate: number; units: number; subtotal: number; fee: number; fee_label: string; total: number; currency: string; security_deposit: number };
  status: string;
  payment_status: string;
  created_at: string;
  info_requested?: boolean;
  customer: { name: string; email: string; phone?: string; country?: string };
  history: { status: string; at: string; note?: string; public_note?: string; by?: string }[];
  has_review?: boolean;
  notes?: string;
  pickup_location?: string;
  driver_age?: number;
  payment_log?: any[];
  deposit_status?: string;
  deposit_log?: { status: string; amount?: number; note?: string; by: string; at: string }[];
  consent?: any;
}
